import { DOCUMENT, Location } from '@angular/common';
import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  MsalInterceptorAuthRequest,
  ProtectedResourceScopes,
} from '@azure/msal-angular/msal.interceptor.config';
import {
  AccountInfo,
  AuthenticationResult,
  BrowserConfigurationAuthError,
  InteractionType,
  StringUtils,
  UrlString,
} from '@azure/msal-browser';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';

interface MatchingResources {
  absoluteResources: string[];
  relativeResources: string[];
}

// reimplementaiton off
// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/src/msal.interceptor.ts
//
@Injectable()
export class AzureB2CAuthInterceptor implements HttpInterceptor {
  private _document?: Document;

  constructor(
    @Inject(MSAL_INTERCEPTOR_CONFIG)
    protected msalInterceptorConfig: MsalInterceptorConfiguration,
    protected msalService: MsalService,
    protected authService: AuthService,
    protected location: Location,
    @Inject(DOCUMENT) document?: any
  ) {
    this._document = document as Document;
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // console.log('AzureB2CAuthInterceptor.intercept()', req.url);

    if (
      this.msalInterceptorConfig.interactionType !== InteractionType.Popup &&
      this.msalInterceptorConfig.interactionType !== InteractionType.Redirect
    ) {
      throw new BrowserConfigurationAuthError(
        'invalid_interaction_type',
        'Invalid interaction type provided to MSAL Interceptor. InteractionType.Popup, InteractionType.Redirect must be provided in the msalInterceptorConfiguration'
      );
    }

    this.msalService.getLogger().verbose('MSAL Interceptor activated');
    const scopes = this.getScopesForEndpoint(req.url, req.method);

    // console.log('AzureB2CAuthInterceptor.intercept()', scopes);

    // If no scopes for endpoint, does not acquire token
    if (!scopes || scopes.length === 0) {
      this.msalService
        .getLogger()
        .verbose('Interceptor - no scopes for endpoint');
      return next.handle(req);
    }

    // Sets account as active account or first account
    let account: AccountInfo;
    if (this.msalService.instance.getActiveAccount()) {
      this.msalService
        .getLogger()
        .verbose('Interceptor - active account selected');
      account = this.msalService.instance.getActiveAccount() as any;
    } else {
      this.msalService
        .getLogger()
        .verbose('Interceptor - no active account, fallback to first account');
      account = this.msalService.instance.getAllAccounts()[0];
    }

    const authRequest =
      typeof this.msalInterceptorConfig.authRequest === 'function'
        ? this.msalInterceptorConfig.authRequest(this.msalService, req, {
            account: account,
          })
        : { ...this.msalInterceptorConfig.authRequest, account };

    this.msalService
      .getLogger()
      .info(`Interceptor - ${scopes.length} scopes found for endpoint`);
    this.msalService
      .getLogger()
      .infoPii(`Interceptor - [${scopes}] scopes found for ${req.url}`);

    //console.log('AzureB2CAuthInterceptor.intercept()', authRequest);

    // Note: For MSA accounts, include openid scope when calling acquireTokenSilent to return idToken
    return this.msalService
      .acquireTokenSilent({ ...authRequest, scopes, account })
      .pipe(
        catchError(() => {
          this.msalService
            .getLogger()
            .error(
              'Interceptor - acquireTokenSilent rejected with error. Invoking interaction to resolve.'
            );
          return this.acquireTokenInteractively(authRequest, scopes);
        }),
        switchMap((result: AuthenticationResult) => {
          if (!result.accessToken) {
            this.msalService
              .getLogger()
              .error(
                'Interceptor - acquireTokenSilent resolved with null access token. Known issue with B2C tenants, invoking interaction to resolve.'
              );
            return this.acquireTokenInteractively(authRequest, scopes);
          }
          return of(result);
        }),
        switchMap((result: AuthenticationResult) => {
          return this.authService.identityToken$.pipe(
            map(m => {
              result.idToken = m;
              return result;
            })
          );
        }),
        switchMap((result: AuthenticationResult) => {
          this.msalService
            .getLogger()
            .verbose('Interceptor - setting authorization headers');

          const headers = new HttpHeaders({
            Authorization: `Bearer ${result.accessToken}`,
            IdToken: result?.idToken,
          });

          const requestClone = req.clone({ headers });
          return next.handle(requestClone);
        })
      );
  }

  /**
   * Invoke interaction for the given set of scopes
   * @param authRequest Request
   * @param scopes Array of scopes for the request
   * @returns Result from the interactive request
   */
  private acquireTokenInteractively(
    authRequest: MsalInterceptorAuthRequest,
    scopes: string[]
  ): Observable<AuthenticationResult> {
    if (this.msalInterceptorConfig.interactionType === InteractionType.Popup) {
      this.msalService
        .getLogger()
        .verbose(
          'Interceptor - error acquiring token silently, acquiring by popup'
        );
      return this.msalService.acquireTokenPopup({
        ...authRequest,
        scopes,
      });
    }
    this.msalService
      .getLogger()
      .verbose(
        'Interceptor - error acquiring token silently, acquiring by redirect'
      );
    const redirectStartPage = window.location.href;
    this.msalService.acquireTokenRedirect({
      ...authRequest,
      scopes,
      redirectStartPage,
    });
    return EMPTY;
  }

  /**
   * Looks up the scopes for the given endpoint from the protectedResourceMap
   * @param endpoint Url of the request
   * @param httpMethod Http method of the request
   * @returns Array of scopes, or null if not found
   *
   */
  private getScopesForEndpoint(
    endpoint: string,
    httpMethod: string
  ): string[] | null {
    this.msalService
      .getLogger()
      .verbose('Interceptor - getting scopes for endpoint');

    // Ensures endpoints and protected resources compared are normalized
    const normalizedEndpoint = this.location.normalize(endpoint);

    const protectedResourcesArray = Array.from(
      this.msalInterceptorConfig.protectedResourceMap.keys()
    );

    const matchingProtectedResources = this.matchResourcesToEndpoint(
      protectedResourcesArray,
      normalizedEndpoint
    );

    // Check absolute urls of resources first before checking relative to prevent incorrect matching where multiple resources have similar relative urls
    if (matchingProtectedResources.absoluteResources.length > 0) {
      return this.matchScopesToEndpoint(
        this.msalInterceptorConfig.protectedResourceMap,
        matchingProtectedResources.absoluteResources,
        httpMethod
      );
    } else if (matchingProtectedResources.relativeResources.length > 0) {
      return this.matchScopesToEndpoint(
        this.msalInterceptorConfig.protectedResourceMap,
        matchingProtectedResources.relativeResources,
        httpMethod
      );
    }

    return null;
  }

  /**
   * Finds resource endpoints that match request endpoint
   * @param protectedResourcesEndpoints
   * @param endpoint
   * @returns
   */
  private matchResourcesToEndpoint(
    protectedResourcesEndpoints: string[],
    endpoint: string
  ): MatchingResources {
    const matchingResources: MatchingResources = {
      absoluteResources: [],
      relativeResources: [],
    };

    protectedResourcesEndpoints.forEach(key => {
      // Normalizes and adds resource to matchingResources.absoluteResources if key matches endpoint. StringUtils.matchPattern accounts for wildcards
      const normalizedKey = this.location.normalize(key);
      if (StringUtils.matchPattern(normalizedKey, endpoint)) {
        matchingResources.absoluteResources.push(key);
      }

      // Get url components for relative urls
      const absoluteKey = this.getAbsoluteUrl(key);
      const keyComponents = new UrlString(absoluteKey).getUrlComponents();
      const absoluteEndpoint = this.getAbsoluteUrl(endpoint);
      const endpointComponents = new UrlString(
        absoluteEndpoint
      ).getUrlComponents();

      // Normalized key should include query strings if applicable
      const relativeNormalizedKey = keyComponents.QueryString
        ? `${keyComponents.AbsolutePath}?${keyComponents.QueryString}`
        : this.location.normalize(keyComponents.AbsolutePath);

      // Add resource to matchingResources.relativeResources if same origin, relativeKey matches endpoint, and is not empty
      if (
        keyComponents.HostNameAndPort === endpointComponents.HostNameAndPort &&
        StringUtils.matchPattern(relativeNormalizedKey, absoluteEndpoint) &&
        relativeNormalizedKey !== '' &&
        relativeNormalizedKey !== '/*'
      ) {
        matchingResources.relativeResources.push(key);
      }
    });

    return matchingResources;
  }

  /**
   * Transforms relative urls to absolute urls
   * @param url
   * @returns
   */
  private getAbsoluteUrl(url: string): string {
    if (!this._document) return '';
    const link = this._document.createElement('a');
    link.href = url;
    return link.href;
  }

  /**
   * Finds scopes from first matching endpoint with HTTP method that matches request
   * @param protectedResourceMap Protected resource map
   * @param endpointArray Array of resources that match request endpoint
   * @param httpMethod Http method of the request
   * @returns
   */
  private matchScopesToEndpoint(
    protectedResourceMap: Map<
      string,
      (string | ProtectedResourceScopes)[] | null
    >,
    endpointArray: string[],
    httpMethod: string
  ): string[] | null {
    const allMatchedScopes: any[] = [];

    // Check each matched endpoint for matching HttpMethod and scopes
    endpointArray.forEach(matchedEndpoint => {
      const scopesForEndpoint: any[] = [];
      const methodAndScopesArray = protectedResourceMap.get(matchedEndpoint);

      // Return if resource is unprotected
      if (methodAndScopesArray === null) {
        allMatchedScopes.push(null);
        return;
      }

      if (methodAndScopesArray)
        methodAndScopesArray.forEach(entry => {
          // Entry is either array of scopes or ProtectedResourceScopes object
          if (typeof entry === 'string') {
            scopesForEndpoint.push(entry);
          } else {
            // Ensure methods being compared are normalized
            const normalizedRequestMethod = httpMethod.toLowerCase();
            const normalizedResourceMethod = entry.httpMethod.toLowerCase();
            // Method in protectedResourceMap matches request http method
            if (normalizedResourceMethod === normalizedRequestMethod) {
              // Validate if scopes comes null to unprotect the resource in a certain http method
              if (entry.scopes === null) {
                allMatchedScopes.push(null);
              } else {
                entry.scopes.forEach(scope => {
                  scopesForEndpoint.push(scope);
                });
              }
            }
          }
        });

      // Only add to all scopes if scopes for endpoint and method is found
      if (scopesForEndpoint.length > 0) {
        allMatchedScopes.push(scopesForEndpoint);
      }
    });

    if (allMatchedScopes.length > 0) {
      if (allMatchedScopes.length > 1) {
        this.msalService
          .getLogger()
          .warning(
            'Interceptor - More than 1 matching scopes for endpoint found.'
          );
      }
      // Returns scopes for first matching endpoint
      return allMatchedScopes[0];
    }

    return null;
  }
}
